import React, { useState, useEffect } from "react";
import {
    Button,
    Checkbox,
    Text,
    Header,
    Loader,
    Flex,
    ArrowLeftIcon,
} from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { useHistory } from "react-router-dom";
import { useSSOAuth } from "./SSOAuthService";
import { getApiBaseUrl, parseJwt } from "./PresenceSubscriptionCard";

const LARGE_TENANT_THRESHOLD = 100;

const GRAPH_SCOPES = [
    "User.Read.All",
    "Presence.Read.All",
    "Calendars.Read",
    "offline_access",
];

const checkboxLabels = {
    importTeamsUsers: "Teams-Benutzer in Mitarbeiterliste importieren",
    keepDetailsUpdated: "Mitarbeiter-Details aktuell halten",
    autoCreateUsers: "Neue Mitarbeiter automatisch hinzufügen",
    autoDeleteUsers: "Ausgeschiedene Mitarbeiter automatisch entfernen",
};

const checkboxDescriptions = {
    importTeamsUsers:
        "Ordnet Ihre Teams-Benutzer bestehenden ebuero-Mitarbeitern anhand von Name und E-Mail zu.",
    keepDetailsUpdated:
        "Wenn sich Name, E-Mail oder Position in Teams ändert, wird dies automatisch in ebuero übernommen.",
    autoCreateUsers:
        "Wenn jemand neu zu Teams hinzukommt, wird er auch in ebuero angelegt.",
    autoDeleteUsers:
        "Wenn jemand aus Teams entfernt wird, wird er auch in ebuero entfernt (mit Vorsicht verwenden).",
};

const SyncSetup = () => {
    const history = useHistory();
    const { getToken, loading: authLoading } = useSSOAuth();

    const [syncOptions, setSyncOptions] = useState({
        importTeamsUsers: true,
        keepDetailsUpdated: true,
        autoCreateUsers: true,
        autoDeleteUsers: false,
        showRealTimeAvailability: true,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [previewLoading, setPreviewLoading] = useState(true);
    const [userCount, setUserCount] = useState(null);
    const [userPreview, setUserPreview] = useState([]);
    const [previewError, setPreviewError] = useState(null);
    const [previewReloadKey, setPreviewReloadKey] = useState(0);

    useEffect(() => {
        app.initialize().then(() => {
            app.notifyAppLoaded();
            app.notifySuccess();
        });
    }, []);

    useEffect(() => {
        if (authLoading) return;
        let cancelled = false;
        setPreviewLoading(true);
        setPreviewError(null);
        (async () => {
            try {
                const token = await getToken(GRAPH_SCOPES);
                // Exclude non-person accounts (shared mailboxes, service accounts,
                // room/equipment resources). Heuristic: real employees have both
                // givenName and surname. Mirrors backend filter in
                // fetchAndUpsertGraphUsers.
                // Requires advanced query: ConsistencyLevel=eventual + $count=true.
                const headers = {
                    Authorization: `Bearer ${token}`,
                    ConsistencyLevel: "eventual",
                };
                // Exclude disabled/terminated accounts (accountEnabled=false) AND
                // non-person accounts (no givenName+surname).
                const filter = encodeURIComponent(
                    "accountEnabled eq true and givenName ne null and surname ne null"
                );
                const listRes = await fetch(
                    `https://graph.microsoft.com/v1.0/users?$count=true&$top=50&$filter=${filter}&$select=displayName,mail,jobTitle,givenName,surname`,
                    { headers }
                );
                if (cancelled) return;

                if (!listRes.ok) {
                    if (listRes.status === 403) setPreviewError("consent");
                    else if (listRes.status === 401) setPreviewError("auth");
                    else setPreviewError("transient");
                } else {
                    const data = await listRes.json();
                    if (typeof data["@odata.count"] === "number") {
                        setUserCount(data["@odata.count"]);
                    }
                    setUserPreview(Array.isArray(data.value) ? data.value : []);
                }
            } catch (e) {
                console.error("User preview fetch failed:", e);
                if (!cancelled) setPreviewError("transient");
            } finally {
                if (!cancelled) setPreviewLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [authLoading, getToken, previewReloadKey]);

    const handleToggle = (key) => {
        setSyncOptions((prev) => {
            const next = { ...prev, [key]: !prev[key] };
            // autoCreateUsers (#3) depends on importTeamsUsers (#1): without the
            // name/email existence check from #1, the scheduler's auto-add loop
            // would duplicate employees. Auto-uncheck #3 when #1 is disabled.
            if (key === "importTeamsUsers" && !next.importTeamsUsers) {
                next.autoCreateUsers = false;
            }
            return next;
        });
    };

    const handleStartSync = async () => {
        setSaving(true);
        setError(null);
        try {
            const token = await getToken(GRAPH_SCOPES);
            const decoded = parseJwt(token);
            const tenantId = decoded ? decoded.tid : null;
            const aadId = decoded ? decoded.oid || decoded.sub : null;
            const baseUrl = getApiBaseUrl();
            const headers = {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            };
            const body = JSON.stringify({ tenant_id: tenantId, aad_id: aadId });

            const configRes = await fetch(`${baseUrl}/MSTeamsUser/syncConfig`, {
                method: "POST",
                headers,
                body: JSON.stringify({
                    tenant_id: tenantId,
                    aad_id: aadId,
                    syncOptions,
                    setupComplete: true,
                }),
            });
            if (!configRes.ok) {
                throw new Error("Sync-Konfiguration konnte nicht gespeichert werden.");
            }

            fetch(`${baseUrl}/MSTeamsUser/renewPresenceSubscription`, {
                method: "POST",
                headers,
                body,
            }).catch((e) => console.error("renewPresenceSubscription failed:", e));

            fetch(`${baseUrl}/MSTeamsUser/triggerFullSync`, {
                method: "POST",
                headers,
                body,
            }).catch((e) => console.error("triggerFullSync failed:", e));

            history.push("/sync/dashboard");
        } catch (e) {
            console.error("Setup failed:", e);
            setError("Einrichtung fehlgeschlagen. Bitte versuchen Sie es erneut.");
            setSaving(false);
        }
    };

    if (authLoading) {
        return (
            <Flex hAlign="center" vAlign="center" style={{ padding: "24px 32px", minHeight: 300 }}>
                <Loader label="Authentifizierung..." />
            </Flex>
        );
    }

    const isLargeTenant = userCount !== null && userCount > LARGE_TENANT_THRESHOLD;

    return (
        <div style={{ padding: "12px 20px", maxWidth: 980, margin: "0 auto" }}>
            <Button
                icon={<ArrowLeftIcon />}
                content="Einstellungen"
                text
                onClick={() => history.push("/setting")}
                style={{ color: "#6264A7", marginBottom: 6 }}
            />

            {/* Full-width purple header bar */}
            <div
                style={{
                    background: "#6264A7",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "6px 6px 0 0",
                }}
            >
                <Header
                    as="h3"
                    content="Teams-Mitarbeiter Sync — Einrichtung"
                    style={{ color: "#fff", fontWeight: 600, margin: 0, fontSize: 15 }}
                />
            </div>

            <div
                style={{
                    background: "#f9f9fb",
                    padding: "12px 16px",
                    borderRadius: "0 0 6px 6px",
                    marginBottom: 12,
                }}
            >
                <Text
                    content="Verbinden Sie Ihren Microsoft 365 Tenant, um Ihre Teams-Benutzer in Ihre ebuero-Mitarbeiterliste zu synchronisieren."
                    style={{ color: "#605e5c", display: "block", marginBottom: 10 }}
                />

                {/* Blue "Was bewirkt das?" info panel */}
                <div
                    style={{
                        background: "#e8f4fd",
                        border: "1px solid #b3d9f2",
                        borderRadius: 6,
                        padding: "10px 14px",
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: "#1a3a5c",
                    }}
                >
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                        Was bewirkt das?
                    </div>
                    <div style={{ marginBottom: 4 }}>Diese App verbindet zwei Dinge:</div>
                    <div style={{ marginBottom: 3 }}>
                        <strong>1. Mitarbeiterliste-Sync</strong> — Ihre Teams-Benutzer (Namen,
                        E-Mails, Positionen) werden in Ihre ebuero-Mitarbeiterliste importiert,
                        damit Sekretärinnen immer aktuelle Kontaktdaten haben.
                    </div>
                    <div>
                        <strong>2. Echtzeit-Verfügbarkeit</strong> — Der Teams-Status (Verfügbar,
                        Beschäftigt, Im Anruf usw.) wird in Echtzeit angezeigt, damit Sekretärinnen
                        wissen, wer Anrufe annehmen kann.
                    </div>
                </div>
            </div>

            {/* Employee preview card */}
            <div
                style={{
                    background: "#fff",
                    borderLeft: "3px solid #6264A7",
                    borderRadius: 6,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    padding: "12px 16px",
                    marginBottom: 12,
                    maxHeight: 165,
                    overflowY: "auto",
                }}
            >
                <div style={{ fontWeight: 600, fontSize: 13, color: "#5b5fc7", marginBottom: 6 }}>
                    Vorschau Ihrer Teams-Benutzer
                </div>

                {previewLoading && (
                    <Loader size="smallest" label="Benutzer werden geladen..." />
                )}

                {!previewLoading && userCount === null && previewError && (
                    <div
                        style={{
                            background:
                                previewError === "consent" ? "#fff8e1" : "#fdecea",
                            border:
                                previewError === "consent"
                                    ? "1px solid #ffe082"
                                    : "1px solid #f5c6cb",
                            borderRadius: 6,
                            padding: "8px 12px",
                            fontSize: 12,
                            color:
                                previewError === "consent" ? "#8a6d00" : "#a4262c",
                        }}
                    >
                        {previewError === "consent" && (
                            <>
                                ⚠ Administrator-Zustimmung fehlt: Bitte bitten Sie Ihren
                                Microsoft 365-Administrator, der App in Azure AD die
                                Berechtigung <strong>User.Read.All</strong> zu erteilen.
                                Sie können die Einrichtung trotzdem fortsetzen — die Vorschau
                                wird verfügbar, sobald die Zustimmung erteilt wurde.
                            </>
                        )}
                        {previewError === "auth" && (
                            <>
                                Anmeldung abgelaufen. Bitte laden Sie die Seite neu und melden
                                Sie sich erneut an.
                            </>
                        )}
                        {previewError === "transient" && (
                            <Flex vAlign="center" gap="gap.small" wrap>
                                <span style={{ flex: 1, minWidth: 200 }}>
                                    Vorschau temporär nicht verfügbar. Sie können die
                                    Einrichtung trotzdem fortsetzen.
                                </span>
                                <Button
                                    size="small"
                                    content="Erneut versuchen"
                                    onClick={() => setPreviewReloadKey((k) => k + 1)}
                                />
                            </Flex>
                        )}
                    </div>
                )}

                {!previewLoading && userCount !== null && (
                    <>
                        <Text
                            size="small"
                            style={{ display: "block", marginBottom: 6, color: "#242424" }}
                        >
                            <strong>{userCount}</strong> Benutzer werden synchronisiert
                            {userCount > 50 && userPreview.length > 0 && (
                                <span style={{ color: "#8a8a8a" }}>
                                    {" "}
                                    (Vorschau der ersten {userPreview.length})
                                </span>
                            )}
                        </Text>

                        {isLargeTenant && (
                            <div
                                style={{
                                    background: "#fff8e1",
                                    border: "1px solid #ffe082",
                                    borderRadius: 6,
                                    padding: "8px 12px",
                                    fontSize: 12,
                                    color: "#8a6d00",
                                    marginBottom: 8,
                                }}
                            >
                                ⚠ Großer Tenant erkannt: Die initiale Synchronisierung kann
                                mehrere Minuten dauern. Sie können dieses Fenster offen lassen —
                                der Sync läuft im Hintergrund weiter.
                            </div>
                        )}

                        {userPreview.length > 0 && (
                            <div>
                                {userPreview.map((u) => (
                                    <div
                                        key={u.id || u.mail || u.displayName}
                                        style={{
                                            padding: "4px 0",
                                            borderBottom: "1px solid #f0f0f0",
                                            fontSize: 12,
                                        }}
                                    >
                                        <div style={{ fontWeight: 600, color: "#242424" }}>
                                            {u.displayName || "—"}
                                        </div>
                                        <div style={{ color: "#8a8a8a", fontSize: 11 }}>
                                            {[u.jobTitle, u.mail].filter(Boolean).join(" · ") ||
                                                "\u00A0"}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Sync options card */}
            <div
                style={{
                    background: "#fff",
                    borderLeft: "3px solid #6264A7",
                    borderRadius: 6,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    padding: "12px 16px",
                    marginBottom: 12,
                }}
            >
                <div style={{ fontWeight: 600, fontSize: 13, color: "#5b5fc7", marginBottom: 8 }}>
                    Mitarbeiterliste (wer auf Ihrer Liste steht)
                </div>

                <Flex column gap="gap.small">
                    {Object.keys(syncOptions)
                        .filter((key) => key !== "showRealTimeAvailability")
                        .map((key) => {
                            // #3 (autoCreateUsers) disabled unless #1 (importTeamsUsers) is on.
                            const disabled =
                                key === "autoCreateUsers" && !syncOptions.importTeamsUsers;
                            return (
                                <div key={key}>
                                    <Checkbox
                                        checked={syncOptions[key]}
                                        disabled={disabled}
                                        label={
                                            <span
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: 13,
                                                    color: disabled ? "#a8a8a8" : undefined,
                                                }}
                                            >
                                                {checkboxLabels[key]}
                                            </span>
                                        }
                                        onChange={() => handleToggle(key)}
                                    />
                                    <Text
                                        style={{
                                            color: "#8a8a8a",
                                            display: "block",
                                            marginLeft: 28,
                                            marginTop: 0,
                                            fontSize: 11,
                                        }}
                                    >
                                        {checkboxDescriptions[key]}
                                    </Text>
                                    {disabled && (
                                        <Text
                                            style={{
                                                color: "#b4531f",
                                                display: "block",
                                                marginLeft: 28,
                                                marginTop: 2,
                                                fontSize: 11,
                                                fontStyle: "italic",
                                            }}
                                        >
                                            Benötigt „{checkboxLabels.importTeamsUsers}“.
                                        </Text>
                                    )}
                                </div>
                            );
                        })}
                </Flex>
            </div>

            {/* Reassurance + CTA in one row */}
            <Flex gap="gap.small" vAlign="center" wrap>
                <Text
                    size="small"
                    style={{ color: "#605e5c", flex: 1, minWidth: 240 }}
                >
                    Sobald gestartet, werden Ihre Teams-Benutzer und deren Verfügbarkeit mit
                    ebuero synchronisiert. Die Verbindung wird automatisch im Hintergrund
                    aufrechterhalten.
                </Text>
                <Button
                    primary
                    content="Speichern & Sync starten"
                    loading={saving}
                    disabled={saving}
                    onClick={handleStartSync}
                    style={{ backgroundColor: "#107c10", color: "white" }}
                />
            </Flex>

            {error && (
                <Text
                    error
                    content={error}
                    style={{ display: "block", marginTop: 8, color: "#c4314b" }}
                />
            )}
        </div>
    );
};

export default SyncSetup;
